import dayjs from 'dayjs/esm/index';
import 'dayjs/esm/locale/ru';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import isSameOrAfterPlugin from 'dayjs/esm/plugin/isSameOrAfter';

import { createSelector } from 'reselect';
import {
  getApplicantBirthDate,
  getApplicantDateOfMarriage,
  isApplicantMarried
} from '../selectors/applicant';
import { getSpouseBirthDate } from '../selectors/spouse';
import {
  isDateBeforeThan,
  isDateInvalid,
  isDateBeforeOrSameThan
} from '../helpers/dateHelpers';
import { ERROR_DATE } from '../constants/errors';
import {
  child,
  getActiveChildSecondParent,
  getChildBirthdate,
  isApplicantChild
} from '../selectors/child';
import {
  DeadSecondParent,
  DivorcedSecondParent,
  PaternitySecondParent
} from '../models/child';

dayjs.locale('ru');
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfterPlugin);

export const validateDateOfMarriageWithSpousesDates = createSelector(
  getApplicantDateOfMarriage,
  getApplicantBirthDate,
  getSpouseBirthDate,
  (value, applicantBirthDate, spouseBirthDate) => {
    const getError = error =>
      error ? ERROR_DATE.BIRTHDATE_EARLY_MARRIAGE_DATE : false;
    const applicantDateValid = !isDateInvalid(applicantBirthDate);
    const spouseDateValid = !isDateInvalid(spouseBirthDate);
    const dateOfMarriageValid = !isDateInvalid(value);
    const spousesDatesValid = applicantDateValid && spouseDateValid;

    if (dateOfMarriageValid) {
      const lessThanApplicant = getError(
        isDateBeforeThan(value, applicantBirthDate)
      );
      const lessThanSpouse = getError(isDateBeforeThan(value, spouseBirthDate));

      if (spousesDatesValid) {
        return lessThanApplicant || lessThanSpouse;
      }
      if (applicantDateValid) {
        return lessThanApplicant;
      }
      if (spousesDatesValid) {
        return lessThanSpouse;
      }
    }

    return false;
  }
);

export const validateChildBirthdateWithPaternityDate = createSelector(
  getChildBirthdate,
  getActiveChildSecondParent,
  (
    childBirthdate,
    { paternityRegisterDate: { value } }: PaternitySecondParent
  ) => {
    const getError = error =>
      error ? ERROR_DATE.BIRTHDATE_EARLY_PATERNITY_DATE : false;

    if (!isDateInvalid(childBirthdate) && !isDateInvalid(value)) {
      return getError(isDateBeforeThan(value, childBirthdate));
    }
    return false;
  }
);

export const validateSecondParentBirthdateAndDeathdate = createSelector(
  getActiveChildSecondParent,
  ({
    birthdate: { value: birthdateValue },
    deathdate: { value: deathdateValue }
  }: DeadSecondParent) => {
    const birthdateValueValid = !isDateInvalid(birthdateValue);
    const deathdateValueValid = !isDateInvalid(deathdateValue);

    const getError = error =>
      error ? ERROR_DATE.DEATHDATE_EARLY_BIRTHDATE : false;

    if (birthdateValueValid && deathdateValueValid) {
      return getError(isDateBeforeOrSameThan(deathdateValue, birthdateValue));
    }
    return false;
  }
);

export const validateMarriageAndDivorceDates = createSelector(
  getActiveChildSecondParent,
  ({
    dateOfMarriage: { value: dateOfMarriageValue },
    dateOfDivorce: { value: dateOfDivorceValue }
  }: DivorcedSecondParent) => {
    const dateOfMarriageValueValid = !isDateInvalid(dateOfMarriageValue);
    const dateOfDivorceValueValid = !isDateInvalid(dateOfDivorceValue);

    const getError = error =>
      error ? ERROR_DATE.DIVORCE_DATE_EARLY_MARRIAGE : false;

    if (dateOfMarriageValueValid && dateOfDivorceValueValid) {
      return getError(
        isDateBeforeOrSameThan(dateOfDivorceValue, dateOfMarriageValue)
      );
    }
    return false;
  }
);

export const validateBirthCertificateIssueDate = createSelector(
  child,
  ({
    birthCertificate: {
      issueDate: { value: issueDateValue }
    },
    birthdate: { value: birthdateValue }
  }) => {
    if (!isDateInvalid(birthdateValue) && !isDateInvalid(issueDateValue)) {
      return isDateBeforeThan(issueDateValue, birthdateValue)
        ? ERROR_DATE.DOCUMENT_DATE_EARLY_BIRTHDATE
        : false;
    }
    return false;
  }
);

export const validateMarriageDateAndApplicantBirthDate = createSelector(
  getActiveChildSecondParent,
  getApplicantBirthDate,
  isApplicantChild,
  isApplicantMarried,
  (
    { dateOfMarriage: { value: dateOfMarriageValue } }: DivorcedSecondParent,
    birthdate,
    isApplicantChild,
    isMarried
  ) => {
    if (isMarried && !isApplicantChild) return false;

    const dateOfMarriageValueValid = !isDateInvalid(dateOfMarriageValue);
    const applicantBirthdateValid = !isDateInvalid(birthdate);

    const getError = error =>
      error ? ERROR_DATE.MARRIAGE_DATE_EARLY_APPLICANT_BIRTHDATE : false;

    if (dateOfMarriageValueValid && applicantBirthdateValid) {
      return getError(isDateBeforeThan(dateOfMarriageValue, birthdate));
    }
    return false;
  }
);
